import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const nav = useNavigate();
    const [state, setState] = useState({ status: "checking", email: "" });
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setState({ status: "invalid", email: "" });
            return;
        }
        api.get(`/auth/reset-password/${token}`)
            .then((r) => setState({ status: "ready", email: r.data.email }))
            .catch((err) => setState({ status: "invalid", email: "", error: formatApiErrorDetail(err.response?.data?.detail) }));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated. Sign in with your new password.");
            nav("/login");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-ph-canvas p-6">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center mb-8">
                    <img src="/players-health-logo.png" alt="Players Health" className="h-9 w-auto" draggable={false} />
                </div>
                <div className="ph-card p-7">
                    {state.status === "checking" && (
                        <div className="text-sm text-ph-muted text-center py-4">Validating link…</div>
                    )}
                    {state.status === "invalid" && (
                        <div className="text-center py-2">
                            <div className="w-12 h-12 rounded-full bg-rose-50 mx-auto flex items-center justify-center mb-4">
                                <AlertCircle className="w-6 h-6 text-rose-600" />
                            </div>
                            <h1 className="text-xl font-semibold text-ph-text">This link isn't valid</h1>
                            <p className="text-sm text-ph-muted mt-2">
                                {state.error || "It may have expired or already been used. Request a new reset link."}
                            </p>
                            <Link to="/forgot-password" className="inline-block mt-6 text-sm text-ph-primary font-medium hover:text-ph-primaryHover" data-testid="request-new-reset-link">
                                Request a new link →
                            </Link>
                        </div>
                    )}
                    {state.status === "ready" && (
                        <>
                            <h1 className="text-xl font-semibold text-ph-text">Choose a new password</h1>
                            <p className="text-sm text-ph-muted mt-1">
                                For <span className="text-ph-text font-medium">{state.email}</span>
                            </p>
                            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-password-form">
                                <div>
                                    <label className="text-xs font-medium text-ph-text mb-1.5 block">New password</label>
                                    <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-10" data-testid="reset-password-input" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-ph-text mb-1.5 block">Confirm password</label>
                                    <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-10" data-testid="reset-password-confirm" />
                                </div>
                                <Button type="submit" disabled={submitting} className="w-full h-10 bg-ph-primary text-white font-medium hover:bg-ph-primaryHover" data-testid="reset-submit-btn">
                                    {submitting ? "Updating…" : "Reset password"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
